// Application stage history — the append-only record of every status an
// application has passed through. This is what the Sankey chart is built from.
//
// Every status write path (manual card/row edits, the Gmail sync in
// backend/main.py, the ghosting state machine) appends a StageEvent instead of
// overwriting. backend/main.py keeps a Python twin of stageFromLabel /
// appendStage — keep the two in sync.

export type Stage =
  | "submitted"
  | "oa"
  | "interview"
  | "offer"
  | "rejected"
  | "ghosted";

export type StageSource = "sync" | "manual" | "auto" | "seed";

export type StageEvent = {
  stage: Stage;
  at: string; // ISO timestamp of when the transition was recorded
  source: StageSource;
  message_id?: string; // sync events: the email that caused this stage
  matched?: string; // sync events: how the card was chosen (thread / role / company / guessed)
};

// Left-to-right order in the Sankey. "No response yet" is the synthetic sink for
// applications still sitting at a non-terminal stage.
export const STAGE_ORDER: Stage[] = [
  "submitted",
  "oa",
  "interview",
  "offer",
  "rejected",
  "ghosted",
];

export const NO_RESPONSE_NODE = "No response yet";

export const STAGE_LABELS: Record<Stage, string> = {
  submitted: "Applied",
  oa: "Online Assessment",
  interview: "Interview",
  offer: "Offer",
  rejected: "Rejected",
  ghosted: "Ghosted",
};

// Reuses the status palette already used by JobCardItem's Badge / Tracker dots.
export const STAGE_COLOR: Record<Stage, string> = {
  submitted: "#3b82f6", // blue-500
  oa: "#10b981", // emerald-500
  interview: "#059669", // emerald-600
  offer: "#eab308", // yellow-500
  rejected: "#f43f5e", // rose-500
  ghosted: "#9ca3af", // gray-400
};

export const NO_RESPONSE_COLOR = "#d6bfa8"; // muted caramel

// Progression rank. Terminal stages (rejected / ghosted) are not ranked — they
// can be reached from any non-terminal stage.
const STAGE_ORDINAL: Partial<Record<Stage, number>> = {
  submitted: 0,
  oa: 1,
  interview: 2,
  offer: 3,
};

// Stages you cannot progress past (used by the monotonic sync guard).
export const TERMINAL_STAGES: ReadonlySet<Stage> = new Set<Stage>([
  "rejected",
  "ghosted",
]);

// Stages that represent a final outcome — an application here is no longer
// "waiting on a reply", so the Sankey does not route it to "No response yet".
export const OUTCOME_STAGES: ReadonlySet<Stage> = new Set<Stage>([
  "offer",
  "rejected",
  "ghosted",
]);

// Mirrors LABEL_MAP in Tracker.tsx plus the manual dropdown values. Accepts the
// model's canonical labels ("Application Received", "Online Assessment"), the
// frontend Status strings, and common aliases. Returns null for "Not applicable"
// / anything unrecognised so callers can skip it.
const LABEL_TO_STAGE: Record<string, Stage> = {
  applied: "submitted",
  submitted: "submitted",
  "application received": "submitted",
  "application submitted": "submitted",
  oa: "oa",
  "online assessment": "oa",
  assessment: "oa",
  interview: "interview",
  offer: "offer",
  rejected: "rejected",
  rejection: "rejected",
  ghosted: "ghosted",
};

export function stageFromLabel(label?: string | null): Stage | null {
  if (!label) return null;
  return LABEL_TO_STAGE[label.trim().toLowerCase()] ?? null;
}

type HistoryCarrier = {
  history?: StageEvent[] | null;
  predicted_label?: string;
  date?: string;
};

// Returns the stored history, or a single seed event derived from the current
// label when a doc predates the history field. Never persists — the real array
// is written the next time the app is edited or synced.
export function seedHistory(item: HistoryCarrier): StageEvent[] {
  if (item.history && item.history.length > 0) return item.history;
  const stage = stageFromLabel(item.predicted_label) ?? "submitted";
  return [
    {
      stage,
      at: item.date || new Date().toISOString(),
      source: "seed",
    },
  ];
}

// Pure. Appends { stage } to history unless it already equals the last stage.
// With monotonic=true (the Gmail sync path), a non-terminal stage is only
// appended when it advances past every ordinal already seen — this stops a
// late-arriving "Application Received" email from rewinding an app that is
// already at "Interview". Terminal stages are always allowed once.
export function appendStage(
  history: StageEvent[],
  stage: Stage,
  source: StageSource,
  opts: { monotonic?: boolean } = {}
): StageEvent[] {
  const prev = history ?? [];
  const last = prev[prev.length - 1];

  if (last && last.stage === stage) return prev;

  if (opts.monotonic) {
    if (TERMINAL_STAGES.has(stage)) {
      if (last && TERMINAL_STAGES.has(last.stage)) return prev;
    } else {
      const incoming = STAGE_ORDINAL[stage] ?? -1;
      const maxSeen = prev.reduce((max, e) => {
        const o = STAGE_ORDINAL[e.stage];
        return o !== undefined && o > max ? o : max;
      }, -1);
      if (incoming <= maxSeen) return prev;
    }
  }

  return [...prev, { stage, at: new Date().toISOString(), source }];
}

export type SankeyInput = {
  nodes: { name: string }[];
  links: { source: number; target: number; value: number }[];
};

// Aggregates every application's stage path into weighted flows. Each app
// contributes one +1 per consecutive stage pair in its (seeded) history; an app
// whose last stage is non-terminal also contributes lastStage -> "No response
// yet" so every application reads as a complete flow.
// Sankey column of each stage. Rejected / Ghosted share the last one.
const SANKEY_RANK: Record<Stage, number> = {
  submitted: 0,
  oa: 1,
  interview: 2,
  offer: 3,
  rejected: 4,
  ghosted: 4,
};

// The app's path with moves backwards undone (a card dragged from Interview
// back to OA, a rejection taken back), so every step goes right and ends at
// the current stage. d3-sankey throws "circular link" on a loop.
export function forwardPath(hist: StageEvent[]): Stage[] {
  const path: Stage[] = [];
  for (const { stage } of hist) {
    while (path.length && SANKEY_RANK[path[path.length - 1]] >= SANKEY_RANK[stage]) path.pop();
    path.push(stage);
  }
  return path;
}

export function buildSankeyData(apps: HistoryCarrier[]): SankeyInput {
  // from -> to -> value. Nested maps so node names may contain spaces.
  const counts = new Map<string, Map<string, number>>();
  const seen = new Set<string>();

  const bump = (from: string, to: string) => {
    if (!counts.has(from)) counts.set(from, new Map());
    const row = counts.get(from)!;
    row.set(to, (row.get(to) ?? 0) + 1);
    seen.add(from);
    seen.add(to);
  };

  for (const app of apps) {
    const path = forwardPath(seedHistory(app));
    for (let i = 0; i < path.length - 1; i++) {
      bump(STAGE_LABELS[path[i]], STAGE_LABELS[path[i + 1]]);
    }
    const lastStage = path[path.length - 1];
    if (!OUTCOME_STAGES.has(lastStage)) {
      bump(STAGE_LABELS[lastStage], NO_RESPONSE_NODE);
    }
  }

  const orderedNames = [
    ...STAGE_ORDER.map((s) => STAGE_LABELS[s]),
    NO_RESPONSE_NODE,
  ].filter((name) => seen.has(name));

  const index = new Map(orderedNames.map((name, i) => [name, i] as const));
  const nodes = orderedNames.map((name) => ({ name }));

  const links: SankeyInput["links"] = [];
  for (const [from, row] of Array.from(counts.entries())) {
    for (const [to, value] of Array.from(row.entries())) {
      const source = index.get(from);
      const target = index.get(to);
      if (source !== undefined && target !== undefined) {
        links.push({ source, target, value });
      }
    }
  }

  return { nodes, links };
}
